import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAddPhotoAlternate,
  MdCheck,
  MdClose,
  MdInfoOutline,
  MdOutlineImageNotSupported,
} from "react-icons/md";
import BankCard from "src/components/BankCard/BankCard";
import { EButtonKind } from "src/components/Button/Button.types";
import ColorPicker from "src/components/ColorPicker/ColorPicker";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import FieldsValidation from "src/components/FieldsValidation/FieldsValidation";
import Modal from "src/components/Modal/Modal";
import ModalHasUnsavedChanges from "src/components/Modal/ModalHasUnsavedChanges";
import CustomSelect from "src/components/Select/CustomSelect";
import TextInput from "src/components/TextInput/TextInput";
import { FILE_TYPES_IMAGES } from "src/constants/file-types";
import { ICON_SIZES } from "src/constants/icons";
import { ASK_CONFIRMATION_ON_SAVE_OR_UPDATE } from "src/helpers/constants";
import {
  registerBankAccount,
  updateBankAccount,
} from "src/helpers/db/bankAccountsHelper";
import {
  getContrastingRandomRgbaColor,
  getMimeTypes,
  getRandomRgbaColor,
  objectHasChanges,
  rgbaObjectToString,
  showNotification,
} from "src/helpers/utils";
import useCache from "src/hooks/useCache";
import useCurrencies from "src/hooks/useCurrencies";
import useMainFilter from "src/hooks/useMainFilter";
import { IAccountModalPayload } from "src/interfaces/modals/newAccountModal";
import useModalsStore from "src/store/modalsStore";
import { v4 as uuidv4 } from "uuid";

const AccountModal: React.FC<IAccountModalPayload> = ({
  accountId,
  initialValues,
  mode,
  onSuccess,
}) => {
  const isEditMode = useMemo(() => mode === "edit", [mode]);
  const [newImageToUpload, setNewImageToUpload] = useState<any>(null);
  const [currentImage, setCurrentImage] = useState<any>(
    initialValues?.image || null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mainFilter, setMainFilter } = useMainFilter();
  const { t } = useTranslation();
  const { currencySelectOptions } = useCurrencies();
  const { clearCache } = useCache();

  const { setShowCreateNewAccountModal, setShowEditAccountModal } =
    useModalsStore();

  const [name, setName] = useState<string>("");
  const [bank, setBank] = useState<string>("");
  const [number, setNumber] = useState<string>("");
  const [bgColor, setBgColor] = useState<any>(
    isEditMode ? initialValues?.bgColor : getRandomRgbaColor(1),
  );
  const [textColor, setTextColor] = useState<any>(
    isEditMode
      ? initialValues?.textColor
      : getContrastingRandomRgbaColor(bgColor),
  );
  const [currency, setCurrency] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const [hasRequiredFieldsCompleted, setHasRequiredFieldsCompleted] =
    useState<boolean>(isEditMode);

  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const closeModal = () => {
    if (isEditMode) {
      setShowEditAccountModal(false, {});
    } else {
      setShowCreateNewAccountModal(false);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return "Editar cuenta";
    }

    return "Agregar cuenta";
  }, [isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      setName(initialValues?.name || "");
      setNumber(initialValues?.number || "");
      setBank(initialValues?.bank || "");
      setCurrency(initialValues?.currency || "");
      setIsDisabled(initialValues?.isDisabled === true);
      setBgColor(initialValues?.bgColor);
      setTextColor(initialValues?.textColor);
      setCurrentImage(initialValues?.image || null);
      // await sleep(500);
    }
  }, []);

  const fieldsToValidate = useMemo(() => {
    return [
      {
        name: t("Nombre"),
        completed: !!name?.trim?.(),
      },
      {
        name: t("Moneda"),
        completed: !!currency?.trim?.(),
      },
    ];
  }, [name, currency]);

  const createNewAccount = async () => {
    setIsRegistering(true);
    const accountId = uuidv4();

    const response = await registerBankAccount({
      account: accountId,
      name,
      bank,
      number,
      currency,
      settings: {
        bg: bgColor,
        color: textColor,
        disabled: isDisabled,
      },
      logo: newImageToUpload,
    });

    await clearCache();

    if (response.success) {
      setShowSaveDialog(false);
      closeModal();
      showNotification({
        message: t("pages:account.notifications.save.success"),
        kind: "success",
      });
    } else {
      showNotification({
        message: t("pages:account.notifications.save.error"),
        kind: "error",
      });
    }

    setIsRegistering(false);
  };

  const updateAccount = async () => {
    setIsUpdating(true);

    const res = await updateBankAccount({
      account: initialValues?.account || "",
      name,
      bank,
      number,
      currency,
      settings: {
        bg: bgColor,
        color: textColor,
        disabled: isDisabled,
        picture: currentImage,
      },
      logo: newImageToUpload || null,
    });

    if (isDisabled && mainFilter?.account === accountId) {
      setMainFilter({ account: "" });
    }
    await clearCache();

    if (res.success) {
      setShowUpdateDialog(false);
      closeModal();
      showNotification({
        message: t("pages:account.notifications.update.success"),
        kind: "success",
      });
    } else {
      showNotification({
        message: t("pages:account.notifications.update.error"),
        kind: "error",
      });
    }

    setIsUpdating(false);
  };

  const hasChanges = useMemo(() => {
    if (isEditMode) {
      return objectHasChanges({
        original: {
          name: initialValues?.name,
          number: initialValues?.number,
          bank: initialValues?.bank,
          currency: initialValues?.currency,
          disabled: initialValues?.isDisabled,
          background: initialValues?.bgColor,
          color: initialValues?.textColor,
          image: initialValues?.image,
        },
        edited: {
          name,
          number,
          bank,
          currency,
          disabled: isDisabled,
          background: bgColor,
          color: textColor,
          image: newImageToUpload || currentImage,
        },
      });
    }

    return false;
  }, [
    initialValues,
    name,
    number,
    bank,
    currency,
    isDisabled,
    bgColor,
    textColor,
    newImageToUpload,
    currentImage,
  ]);

  const saveOrUpdateButtonIsDisabled = useMemo(() => {
    if (!hasRequiredFieldsCompleted) {
      return true;
    }

    if (isEditMode) {
      return !hasChanges;
    }

    return false;
  }, [hasChanges, isEditMode, hasRequiredFieldsCompleted]);

  const imagePreview = useMemo(() => {
    if (newImageToUpload) {
      return URL.createObjectURL(newImageToUpload);
    }

    return currentImage?.thumbnailUrl || "";
  }, [newImageToUpload, currentImage]);

  return (
    <>
      <Modal
        onEscape={() => {}}
        className={clsx(["md:!max-w-[530px]", "md:!w-full"])}
      >
        <Modal.Header onClose={() => closeModal()} title={modalTitle} />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "gap-4",
              "md:gap-6",
            ])}
          >
            <input
              type="file"
              multiple={false}
              accept={getMimeTypes(FILE_TYPES_IMAGES)}
              ref={fileInputRef}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                // setIsLoading(true);

                setNewImageToUpload(e?.target?.files?.[0]);
                // await handleFileChange(e?.target?.files);
                // setIsLoading(false);
                // if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="hidden"
            />
            <>
              <div className="flex flex-col gap-4 md:min-w-[350px]">
                <div className="flex flex-col gap-4">
                  <div
                    className={clsx(["flex", "flex-col", "w-full", "gap-4"])}
                  >
                    <BankCard
                      disabled={isDisabled}
                      account=""
                      number={
                        `${number}`.trim() ||
                        t("pages:account.fields.number_placeholder")
                      }
                      owner={name}
                      bank={`${bank}`.trim()}
                      name={
                        `${name}`.trim() ||
                        t("pages:account.fields.name_placeholder")
                      }
                      variant="custom"
                      customStyles={{
                        background: bgColor ? rgbaObjectToString(bgColor) : {},
                        color: textColor ? rgbaObjectToString(textColor) : {},
                      }}
                      currency={currency}
                      actions={[
                        {
                          onClick: () => openFileDialog(),
                          icon: <MdAddPhotoAlternate />,
                          className: clsx([
                            "!bg-transparent",
                            "!text-current",
                            "md:!h-[40px]",
                            "md:!w-[40px]",
                            "md:!max-w-[40px]",
                            "md:!min-w-[40px]",
                            "md:!max-h-[40px]",
                            "md:!min-h-[40px]",
                            "!border-0",
                            "!shadow-none",
                          ]),
                        },
                        {
                          icon: <MdOutlineImageNotSupported />,
                          onClick: () => {
                            setCurrentImage(null);
                            setNewImageToUpload(null);
                          },
                          className: clsx([
                            "!bg-transparent",
                            "!text-current",
                            "md:!h-[40px]",
                            "md:!w-[40px]",
                            "md:!max-w-[40px]",
                            "md:!min-w-[40px]",
                            "md:!max-h-[40px]",
                            "md:!min-h-[40px]",
                            "!border-0",
                            "!shadow-none",
                          ]),
                          hide: !newImageToUpload || currentImage,
                        },
                      ]}
                      image={imagePreview}
                    />
                    {isDisabled && (
                      <div
                        className={clsx([
                          "flex-shrink",
                          // "flex",
                          "text-xs",
                          "flex-grow",
                          "hidden",
                        ])}
                      >
                        <div
                          className={clsx([
                            "flex",
                            "w-full",
                            "flex-row",
                            "gap-1",
                          ])}
                        >
                          <MdInfoOutline
                            size={ICON_SIZES.XS}
                            className={clsx(["opacity-30"])}
                          />
                          {t("info_blocks:inactive_accounts.how_to_identify")}
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-2",
                    ])}
                  >
                    <ColorPicker
                      onChange={(color: any) => {
                        setBgColor(color.rgb);
                      }}
                      textColor={textColor}
                      color={bgColor}
                      label={t("pages:account.fields.bg")}
                      showText={false}
                    />
                    <ColorPicker
                      onChange={(color: any) => {
                        setTextColor(color.rgb);
                      }}
                      textColor={bgColor}
                      color={textColor}
                      label={t("pages:account.fields.color")}
                      showText={false}
                    />
                  </div>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-2",
                    ])}
                  >
                    <TextInput
                      placeholder={t("pages:account.fields.name_placeholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      label={t("pages:account.fields.name")}
                      type="text"
                      required
                      // disabled={isPerformingAction}
                      autoComplete="name nickname"
                    />
                    <TextInput
                      placeholder={t("pages:account.fields.number_placeholder")}
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      label={t("pages:account.fields.number")}
                      type="text"
                      // disabled={isPerformingAction}
                    />
                  </div>
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-2",
                    ])}
                  >
                    <TextInput
                      placeholder={t("pages:account.fields.bank_placeholder")}
                      value={bank}
                      onChange={(e) => setBank(e.target.value)}
                      label={t("pages:account.fields.bank")}
                      type="text"
                      // disabled={isPerformingAction}
                    />
                    <CustomSelect
                      required
                      label={t("pages:account.fields.currency")}
                      placeholder={t(
                        "pages:account.fields.currency_placeholder",
                      )}
                      options={currencySelectOptions}
                      value={currency}
                      onChange={(e: any) => setCurrency(e?.value)}
                      // disabled={isPerformingAction}
                      useValueAsLabel={false}
                      allowSearchModal
                      withIcons
                    />
                  </div>
                  <div>
                    <CustomSelect
                      required
                      label={t("pages:account.fields.status")}
                      placeholder={t("pages:account.fields.status_placeholder")}
                      isSearchable={false}
                      options={[
                        {
                          label: t("pages:account.statuses.inactive"),
                          value: "true",
                        },
                        {
                          label: t("pages:account.statuses.active"),
                          value: "false",
                        },
                      ]}
                      value={`${isDisabled}`}
                      onChange={(e: any) => setIsDisabled(e?.value === "true")}
                      // disabled={isPerformingAction}
                      useValueAsLabel={false}
                      isClearable={false}
                    />
                  </div>
                </div>
              </div>
              <FieldsValidation
                message={t("info_blocks:fields_validation.incomplete.message")}
                fieldsToValidate={fieldsToValidate}
                onChange={setHasRequiredFieldsCompleted}
                showFields={false}
                showButton
              />
            </>
          </div>
        </Modal.Body>
        <Modal.Footer
          leftContent={
            isEditMode && hasChanges ? <ModalHasUnsavedChanges /> : null
          }
          actions={[
            {
              text: isEditMode
                ? t("common:buttons.update")
                : t("common:buttons.save"),
              onClick: () => {
                if (isEditMode) {
                  if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                    setShowUpdateDialog(true);
                  } else {
                    updateAccount();
                  }
                } else {
                  if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                    setShowSaveDialog(true);
                  } else {
                    createNewAccount();
                  }
                }
              },

              icon: <MdCheck size={ICON_SIZES.SM} />,
              kind: isEditMode ? EButtonKind.EDIT : EButtonKind.SAVE,
              isLoading: isUpdating || isRegistering,
              disabled:
                saveOrUpdateButtonIsDisabled || isUpdating || isRegistering,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => closeModal(),
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isUpdating || isRegistering,
            },
          ]}
        />
      </Modal>

      {showUpdateDialog && (
        <ConfirmDialog
          description={t("pages:account.questions.update")}
          title={t("dialogs:titles.update")}
          onClose={() => {
            if (!isUpdating) {
              setShowUpdateDialog(false);
            }
          }}
          onConfirm={() => updateAccount()}
          confirmKind={EButtonKind.EDIT}
          isLoading={isUpdating}
        />
      )}

      {showSaveDialog && (
        <ConfirmDialog
          description={t("pages:account.questions.save")}
          title={t("dialogs:titles.save")}
          onClose={() => {
            if (!isRegistering) {
              setShowSaveDialog(false);
            }
          }}
          onConfirm={() => createNewAccount()}
          confirmKind={EButtonKind.SAVE}
          isLoading={isRegistering}
        />
      )}
    </>
  );
};

export default AccountModal;
